/**
 * Builds the full worklist fetch payload, including optional DOW date bounds.
 *
 * Date filtering is a top-level API concern (`date_from_filename_*`), separate
 * from the predicate tree in `filters`, so both pieces are assembled here.
 */

import { v4 as uuidv4 } from "uuid";
import { OrderByItem } from "../../../api/bigQuery/pharmaStpFileOrders";
import { FilterValues } from "../../../data-model/filterValues";
import {
  buildWorklistFilters,
  CallingWorklistView,
  WorklistFilterGroup,
} from "./buildWorklistFilters";
import { processFilterData } from "./processFilterData";

/**
 * Request body for calling-worklist fetches against the Pharma STP file API.
 *
 * DOW date bounds travel as top-level `date_from_filename_*` fields; all other
 * panel filters live inside the `filters` predicate tree.
 */
export interface PharmaStpFileFilterPayload {
  /** Inclusive start of the DOW range extracted from filenames (YYYYMMDD). */
  date_from_filename_start?: string;
  /** Inclusive end of the DOW range extracted from filenames (YYYYMMDD). */
  date_from_filename_end?: string;
  /** Predicate tree compiled to the query's WHERE clause by the backend. */
  filters?: WorklistFilterGroup;
  page: number;
  page_size: number;
  request_id?: string;
  order_by?: OrderByItem[];
}

/** Default sort applied to every worklist fetch. */
const DEFAULT_ORDER_BY: OrderByItem[] = [
  { column: "filename", direction: "DESC" },
  { column: "patient_mrn", direction: "ASC" },
  { column: "drug", direction: "ASC" },
];

export interface BuildWorklistFetchPayloadInput {
  /** Worklist the payload is for; defaults to the non-ePA list. */
  view?: CallingWorklistView;
  /** When true, fetch archived orders and ignore panel filters (including DOW). */
  isArchiveView?: boolean;
  /** Filters the user applied through the table's filter panel. */
  validFilters?: FilterValues[];
  /** Comma-separated MRN search box contents. */
  searchTerm?: string;
  /** Page size for the request. */
  pageSize: number;
  /** 1-based page number; defaults to 1. */
  page?: number;
}

/**
 * Builds a complete Pharma STP file fetch payload for the calling worklist.
 *
 * When the user picks a DOW date range, it is sent as
 * `date_from_filename_start` / `date_from_filename_end` so the backend can
 * match dates embedded in filenames. With no DOW selection, those fields are
 * omitted so the list is not date-bounded.
 *
 * @input input - View, archive flag, filters, search text and pagination
 * @return Payload ready to POST to the STP file data endpoint
 */
export const buildWorklistFetchPayload = (
  input: BuildWorklistFetchPayloadInput,
): PharmaStpFileFilterPayload => {
  const {
    view = CallingWorklistView.NON_EPA,
    isArchiveView = false,
    validFilters = [],
    searchTerm = "",
    pageSize,
    page = 1,
  } = input;

  const { dateRange } = isArchiveView
    ? { dateRange: undefined }
    : processFilterData(validFilters);

  return {
    ...(dateRange
      ? {
          date_from_filename_start: dateRange.startDate,
          date_from_filename_end: dateRange.endDate,
        }
      : {}),
    filters: buildWorklistFilters({
      view,
      isArchiveView,
      validFilters,
      searchTerm,
    }),
    page,
    page_size: pageSize,
    request_id: uuidv4(),
    order_by: DEFAULT_ORDER_BY,
  };
};
